import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { EmptyState } from '../components/ui/EmptyState';
import { Switch } from '../components/ui/Switch';
import { api } from '../lib/api';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { Loader } from '../components/ui/Spinner';

type Shift = { id: string; name: string; startTime: string; endTime: string; isActive: boolean };

function ShiftForm({ initial, onSubmit, onCancel, pending }: { initial?: Shift; onSubmit: (v: { name: string; startTime: string; endTime: string; isActive: boolean }) => void; onCancel: () => void; pending: boolean }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [startTime, setStartTime] = useState(initial?.startTime.slice(0, 5) ?? '09:30');
  const [endTime, setEndTime] = useState(initial?.endTime.slice(0, 5) ?? '18:30');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  return (
    <form className="card mb-6 grid gap-4 sm:grid-cols-3" onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit({ name, startTime, endTime, isActive }); }}>
      <label className="label">Shift name<input className="field mt-1" required placeholder="e.g. General" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="label">Start time<input type="time" className="field mt-1" required value={startTime} onChange={(e) => setStartTime(e.target.value)} /></label>
      <label className="label">End time<input type="time" className="field mt-1" required value={endTime} onChange={(e) => setEndTime(e.target.value)} /></label>
      {initial && <label className="label flex items-center gap-3">Active<Switch checked={isActive} onChange={setIsActive} /></label>}
      <div className="flex items-end gap-2 sm:col-span-3">
        <button className="btn-primary" disabled={pending}>Save</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export function ShiftsPage() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Shift | null>(null);
  const shifts = useQuery({ queryKey: ['shifts'], queryFn: async () => (await api.get<Shift[]>('/api/shifts')).data });

  const create = useMutation({
    mutationFn: (body: { name: string; startTime: string; endTime: string }) => api.post('/api/shifts', body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['shifts'] }); success('Shift created'); setOpen(false); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not create shift')),
  });
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; name: string; startTime: string; endTime: string; isActive: boolean }) => api.patch(`/api/shifts/${id}`, body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['shifts'] }); success('Shift updated'); setEditing(null); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update shift')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/shifts/${id}`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['shifts'] }); success('Shift deleted'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not delete — a branch may still use it')),
  });

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div><h1 className="text-3xl font-bold">Shifts</h1><p className="mt-1 text-muted">Working hours templates assigned to branches.</p></div>
        <button className="btn-primary" onClick={() => { setOpen(!open); setEditing(null); }}><Plus size={18} /> New</button>
      </div>

      {open && <ShiftForm onSubmit={(v) => create.mutate(v)} onCancel={() => setOpen(false)} pending={create.isPending} />}

      {shifts.isLoading && <Loader />}
      {!shifts.isLoading && shifts.data?.length === 0 && <EmptyState title="No shifts yet" />}
      <div className="space-y-3">
        {shifts.data?.map((shift) =>
          editing?.id === shift.id ? (
            <div className="card" key={shift.id}><ShiftForm initial={shift} onSubmit={(v) => update.mutate({ id: shift.id, ...v })} onCancel={() => setEditing(null)} pending={update.isPending} /></div>
          ) : (
            <article key={shift.id} className="card flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Clock size={18} className="shrink-0 text-muted" />
                <div className="min-w-0"><strong className={shift.isActive ? '' : 'text-muted line-through'}>{shift.name}</strong><p className="mt-0.5 text-sm text-muted">{shift.startTime.slice(0, 5)} – {shift.endTime.slice(0, 5)}</p></div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button className="btn-secondary !px-3" aria-label="Edit" onClick={() => { setEditing(shift); setOpen(false); }}><Pencil size={16} /></button>
                <button className="btn-secondary !px-3" aria-label="Delete" onClick={() => { if (confirm(`Delete "${shift.name}"?`)) remove.mutate(shift.id); }}><Trash2 size={16} /></button>
              </div>
            </article>
          ),
        )}
      </div>
    </>
  );
}
